import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, Users, Loader2, AlertCircle, Grid3X3, List, Printer, ShieldCheck, AlertTriangle, User, DoorOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import apiService from '@/services/api';
import { MasterTimetableUpload } from '@/components/timetable/MasterTimetableUpload';

const shortName = (full) => {
  const parts = (full || '').replace(/^(Mr|Mrs|Ms|Miss|Fr|Dr|Sr|Br|Rev)\.?\s+/i, '').trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? `${parts[0][0]}. ${parts[parts.length - 1]}` : parts[0] || '';
};
import { getCurrentUser } from '@/data/users';

const PERIODS = [
  { number: 1, start: "7:30", end: "8:30", label: "P1" },
  { number: 2, start: "8:30", end: "9:30", label: "P2" },
  { number: 3, start: "9:45", end: "10:45", label: "P3" },
  { number: 4, start: "10:45", end: "11:45", label: "P4" },
  { number: 5, start: "1:00", end: "1:50", label: "P5" },
  { number: 6, start: "1:50", end: "2:40", label: "P6" },
  { number: 7, start: "2:40", end: "3:30", label: "P7" },
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const SUBJECT_COLORS = {
  'Mathematics': 'bg-blue-100 border-blue-400 text-blue-800',
  'Calculus': 'bg-blue-100 border-blue-400 text-blue-800',
  'Statistics': 'bg-blue-50 border-blue-300 text-blue-700',
  'English': 'bg-green-100 border-green-400 text-green-800',
  'ICT': 'bg-purple-100 border-purple-400 text-purple-800',
  'Computer': 'bg-purple-100 border-purple-400 text-purple-800',
  'Chemistry': 'bg-red-100 border-red-400 text-red-800',
  'Physics': 'bg-orange-100 border-orange-400 text-orange-800',
  'Biology': 'bg-teal-100 border-teal-400 text-teal-800',
  'Science': 'bg-teal-50 border-teal-300 text-teal-700',
  'History': 'bg-amber-100 border-amber-400 text-amber-800',
  'Geography': 'bg-emerald-100 border-emerald-400 text-emerald-800',
  'French': 'bg-indigo-100 border-indigo-400 text-indigo-800',
  'Economics': 'bg-cyan-100 border-cyan-400 text-cyan-800',
  'Accounting': 'bg-lime-100 border-lime-400 text-lime-800',
  'Agriculture': 'bg-yellow-100 border-yellow-400 text-yellow-800',
  'PE': 'bg-pink-100 border-pink-400 text-pink-800',
  'Social Science': 'bg-rose-100 border-rose-400 text-rose-800',
  'Development': 'bg-sky-100 border-sky-400 text-sky-800',
  'Technology': 'bg-violet-100 border-violet-400 text-violet-800',
  'Religious': 'bg-stone-100 border-stone-400 text-stone-800',
  'default': 'bg-gray-100 border-gray-400 text-gray-800',
};

const getSubjectColor = (subject) => {
  if (!subject) return SUBJECT_COLORS['default'];
  for (const [key, color] of Object.entries(SUBJECT_COLORS)) {
    if (key !== 'default' && subject.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return SUBJECT_COLORS['default'];
};

const sortClasses = (a, b) => {
  const yearA = parseInt(a.match(/\d+/)?.[0] || '0');
  const yearB = parseInt(b.match(/\d+/)?.[0] || '0');
  if (yearA !== yearB) return yearA - yearB;
  return a.localeCompare(b);
};

export default function MasterTimetablePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masterSchedule, setMasterSchedule] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [viewMode, setViewMode] = useState('class');
  const [allClasses, setAllClasses] = useState([]);
  const [conflictCheck, setConflictCheck] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [selectedDay, setSelectedDay] = useState('Monday');

  const currentUser = getCurrentUser() || {};
  const isPrincipal = ['principal', 'academic_principal', 'admin'].includes(currentUser.role) || (currentUser.email || '').toLowerCase() === 'roytari@spc.edu.vu';
  const [reloadKey, setReloadKey] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const uploadCard = <MasterTimetableUpload onImported={() => setReloadKey(k => k + 1)} result={uploadResult} onResult={setUploadResult} />;

  const runConflictCheck = async () => {
    setCheckingConflicts(true);
    try {
      const response = await apiService.get('/timetable/conflict-check');
      setConflictCheck(response.data);
    } catch (err) {
      setConflictCheck({ error: err.message });
    } finally {
      setCheckingConflicts(false);
    }
  };

  useEffect(() => {
    const fetchMasterTimetable = async () => {
      if (!isPrincipal) {
        setError('Access denied. Only principals and academic principals can view the master timetable.');
        setLoading(false);
        return;
      }
      try {
        if (reloadKey === 0) setLoading(true);
        setError(null);
        const response = await apiService.request('/api/timetable/master');
        setMasterSchedule(response.schedule_data);
        setTeachers(response.teachers || []);
        setRooms(response.rooms || []);
        const classes = Object.keys(response.schedule_data || {}).sort(sortClasses);
        setAllClasses(classes);
        if (classes.length > 0) setSelectedClass(classes[0]);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load master timetable');
        setLoading(false);
      }
    };
    fetchMasterTimetable();
  }, [isPrincipal, reloadKey]);

  // Build teacher schedule from master data
  const teacherSchedule = useMemo(() => {
    if (!masterSchedule || !selectedTeacher) return null;
    const schedule = {};
    for (const [className, classData] of Object.entries(masterSchedule)) {
      for (const day of DAYS) {
        const periods = classData[day] || [];
        for (const p of periods) {
          if (p.teacher_email === selectedTeacher || p.teacher === selectedTeacher) {
            if (!schedule[day]) schedule[day] = [];
            schedule[day].push({ ...p, class: p.class || className });
          }
        }
      }
    }
    return schedule;
  }, [masterSchedule, selectedTeacher]);

  // Build room schedule from master data
  const roomSchedule = useMemo(() => {
    if (!masterSchedule || !selectedRoom) return null;
    const schedule = {};
    for (const [className, classData] of Object.entries(masterSchedule)) {
      for (const day of DAYS) {
        const periods = classData[day] || [];
        for (const p of periods) {
          if (p.room === selectedRoom) {
            if (!schedule[day]) schedule[day] = [];
            schedule[day].push({ ...p, class: p.class || className });
          }
        }
      }
    }
    return schedule;
  }, [masterSchedule, selectedRoom]);

  // Get teacher info
  const selectedTeacherInfo = useMemo(() => {
    return teachers.find(t => t.email === selectedTeacher);
  }, [teachers, selectedTeacher]);

  // Print helpers
  const buildPrintTable = (scheduleGetter, title, subtitle) => {
    const rows = PERIODS.map(period => {
      let breakRow = '';
      if (period.number === 3) breakRow = '<tr class="break-row"><td colspan="6">BREAK (9:30 - 9:45)</td></tr>';
      let lunchRow = '';
      if (period.number === 5) lunchRow = '<tr class="lunch-row"><td colspan="6">LUNCH (11:45 - 1:00 PM)</td></tr>';

      const cells = DAYS.map(day => {
        const data = scheduleGetter(day, period.number);
        if (!data) return '<td class="subject-cell">-</td>';
        return `<td class="subject-cell">
          <div class="subject-name">${data.subject || '-'}</div>
          ${data.teacher ? `<div class="teacher-name">${shortName(data.teacher)}</div>` : ''}
          ${data.class ? `<div class="class-name">${data.class}</div>` : ''}
          ${data.room && data.room !== 'TBD' ? `<div class="room-name">${data.room}</div>` : ''}
        </td>`;
      }).join('');

      return `${breakRow}${lunchRow}<tr>
        <td class="period-cell">P${period.number}<br><span class="period-time">${period.start} - ${period.end}</span></td>
        ${cells}
      </tr>`;
    }).join('');

    return `<!DOCTYPE html><html><head><title>${title}</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;padding:20px;color:#333}
      .header{text-align:center;margin-bottom:20px;border-bottom:2px solid #1e40af;padding-bottom:15px}
      .header h1{color:#1e40af;font-size:24px;margin-bottom:5px}
      .header h2{color:#059669;font-size:20px;margin-bottom:5px}
      .header p{color:#666;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-size:11px}
      th,td{border:1px solid #333;padding:8px 4px;text-align:center;vertical-align:top}
      th{background-color:#1e40af;color:white;font-weight:bold;font-size:12px}
      th.period-col{background-color:#059669;width:80px}
      .period-cell{background-color:#f3f4f6;font-weight:bold}
      .period-time{font-size:9px;color:#666}
      .subject-name{font-weight:bold;color:#1e40af}
      .teacher-name{font-size:9px;color:#666;margin-top:2px}
      .class-name{font-size:9px;color:#059669;font-weight:bold;margin-top:1px}
      .room-name{font-size:9px;color:#7c3aed;margin-top:1px}
      .break-row{background-color:#fef3c7;font-weight:bold;color:#92400e}
      .lunch-row{background-color:#dbeafe;font-weight:bold;color:#1e40af}
      .footer{margin-top:20px;text-align:center;font-size:10px;color:#666;border-top:1px solid #ccc;padding-top:10px}
      @media print{body{padding:10px}@page{margin:1cm}}
    </style></head><body>
      <div class="header"><h1>St Patrick's College</h1><h2>${title}</h2><p>${subtitle}</p></div>
      <table><thead><tr><th class="period-col">Period</th>${DAYS.map(d => `<th>${d}</th>`).join('')}</tr></thead>
      <tbody>${rows}</tbody></table>
      <div class="footer"><p>Printed on ${new Date().toLocaleDateString()} | Penama Education Portal</p></div>
    </body></html>`;
  };

  const handlePrint = (title, subtitle, scheduleGetter) => {
    const html = buildPrintTable(scheduleGetter, title, subtitle);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const handlePrintClass = () => {
    if (!selectedClass || !masterSchedule?.[selectedClass]) return;
    const classSchedule = masterSchedule[selectedClass];
    handlePrint(
      `${selectedClass} - Timetable 2026`,
      'Academic Year 2026 | Penama Province, Vanuatu',
      (day, periodNum) => {
        const periods = classSchedule[day] || [];
        return periods.find(p => p.period === periodNum) || null;
      }
    );
  };

  const handlePrintTeacher = () => {
    if (!teacherSchedule || !selectedTeacherInfo) return;
    handlePrint(
      `${selectedTeacherInfo.name} - Teaching Schedule 2026`,
      `${selectedTeacherInfo.subjects?.join(', ')} | Academic Year 2026`,
      (day, periodNum) => {
        const periods = teacherSchedule[day] || [];
        return periods.find(p => p.period === periodNum) || null;
      }
    );
  };

  const handlePrintRoom = () => {
    if (!roomSchedule || !selectedRoom) return;
    handlePrint(
      `${selectedRoom} - Room Schedule 2026`,
      'Academic Year 2026 | Penama Province, Vanuatu',
      (day, periodNum) => {
        const periods = roomSchedule[day] || [];
        return periods.find(p => p.period === periodNum) || null;
      }
    );
  };

  // Render a cell for any schedule view
  const renderCell = (schedule, day, periodNumber, showField) => {
    if (!schedule?.[day]) return <div className="text-xs text-gray-300 text-center">-</div>;
    const periodData = schedule[day].find(p => p.period === periodNumber);
    if (!periodData?.subject) return <div className="text-xs text-gray-300 text-center">-</div>;
    const colorClass = getSubjectColor(periodData.subject);
    return (
      <div className={`p-1.5 rounded border-l-4 ${colorClass}`} data-testid={`cell-${day}-p${periodNumber}`}>
        <div className="font-semibold text-xs leading-tight">{periodData.subject}</div>
        {showField === 'teacher' && (
          <div className="text-xs opacity-70 truncate mt-0.5">{shortName(periodData.teacher)}</div>
        )}
        {showField === 'class' && (
          <div className="text-xs opacity-70 truncate mt-0.5 font-medium">{periodData.class || ''}</div>
        )}
        {periodData.room && periodData.room !== 'TBD' && (
          <div className="text-xs text-violet-600 mt-0.5 truncate">{periodData.room}</div>
        )}
      </div>
    );
  };

  // Master grid cell (compact)
  const renderMasterGridCell = (className, day, periodNumber) => {
    if (!masterSchedule?.[className]?.[day]) return null;
    const periodData = masterSchedule[className][day].find(p => p.period === periodNumber);
    if (!periodData?.subject) return <div className="text-xs text-gray-200 text-center">-</div>;
    const colorClass = getSubjectColor(periodData.subject);
    return (
      <div className={`p-0.5 rounded border-l-2 ${colorClass} text-xs`}>
        <div className="font-medium truncate">{periodData.subject?.substring(0, 10)}</div>
        <div className="truncate opacity-60">{shortName(periodData.teacher)}</div>
      </div>
    );
  };

  // Period rows with break/lunch
  const renderScheduleTable = (schedule, showField) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            <th className="border border-slate-600 p-3 text-left font-semibold min-w-[100px]">
              <Clock className="w-4 h-4 inline mr-2" />Period
            </th>
            {DAYS.map(day => (
              <th key={day} className="border border-slate-600 p-3 text-center font-semibold min-w-[150px]">{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERIODS.map(period => (
            <React.Fragment key={period.number}>
              {period.number === 3 && (
                <tr className="bg-amber-50">
                  <td colSpan={6} className="border p-2 text-center text-amber-700 font-medium text-sm">
                    BREAK (9:30 - 9:45)
                  </td>
                </tr>
              )}
              {period.number === 5 && (
                <tr className="bg-blue-50">
                  <td colSpan={6} className="border p-2 text-center text-blue-700 font-medium text-sm">
                    LUNCH (11:45 - 1:00 PM)
                  </td>
                </tr>
              )}
              <tr className="hover:bg-gray-50">
                <td className="border p-2 bg-slate-50">
                  <div className="font-bold text-slate-800">{period.label}</div>
                  <div className="text-xs text-slate-500">{period.start} - {period.end}</div>
                </td>
                {DAYS.map(day => (
                  <td key={`${day}-${period.number}`} className="border p-1.5">
                    {renderCell(schedule, day, period.number, showField)}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="master-timetable-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading master timetable...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="master-timetable-page">
        {isPrincipal && uploadCard}
        <Alert variant={isPrincipal ? "default" : "destructive"} data-testid="master-timetable-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="master-timetable-page">
      {uploadCard}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-8 h-8 text-blue-600" />
            Master Timetable 2026
          </h1>
          <p className="text-gray-600 mt-1">
            {currentUser.school_name || "St Patrick's College"} | {allClasses.length} Classes | {teachers.length} Teachers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{allClasses.length} Classes</Badge>
          <Badge variant="outline">{teachers.length} Teachers</Badge>
          <Badge variant="outline">{rooms.length} Rooms</Badge>
          <Button
            onClick={runConflictCheck}
            variant="outline"
            disabled={checkingConflicts}
            data-testid="conflict-check-btn"
          >
            {checkingConflicts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Check Conflicts
          </Button>
        </div>
      </div>

      {/* Conflict Results */}
      {conflictCheck && (
        <Card className={conflictCheck.summary?.status === 'clean' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              {conflictCheck.summary?.status === 'clean' ? (
                <><ShieldCheck className="w-5 h-5 text-green-600" /><span className="text-green-800">No Conflicts Found</span></>
              ) : (
                <><AlertTriangle className="w-5 h-5 text-red-600" /><span className="text-red-800">Conflicts Detected</span></>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="text-center p-2 bg-white rounded"><div className="text-2xl font-bold text-blue-600">{conflictCheck.summary?.total_timetables || 0}</div><div className="text-gray-600">Timetables</div></div>
              <div className="text-center p-2 bg-white rounded"><div className="text-2xl font-bold text-purple-600">{conflictCheck.summary?.total_scheduled_periods || 0}</div><div className="text-gray-600">Total Periods</div></div>
              <div className="text-center p-2 bg-white rounded"><div className={`text-2xl font-bold ${conflictCheck.summary?.teacher_conflicts > 0 ? 'text-red-600' : 'text-green-600'}`}>{conflictCheck.summary?.teacher_conflicts || 0}</div><div className="text-gray-600">Teacher Conflicts</div></div>
              <div className="text-center p-2 bg-white rounded"><div className={`text-2xl font-bold ${conflictCheck.summary?.room_conflicts > 0 ? 'text-red-600' : 'text-green-600'}`}>{conflictCheck.summary?.room_conflicts || 0}</div><div className="text-gray-600">Room Conflicts</div></div>
            </div>
            {conflictCheck.teacher_conflicts?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-red-800 mb-2">Teacher Conflicts:</h4>
                <div className="space-y-2">
                  {conflictCheck.teacher_conflicts.map((c, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border border-red-200">
                      <span className="font-medium">{c.teacher}</span> - {c.day} Period {c.period}:
                      <span className="text-red-600 ml-1">{c.classes?.map(cl => cl.class).join(' vs ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {conflictCheck.room_conflicts?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-orange-800 mb-2">Room Conflicts:</h4>
                <div className="space-y-2">
                  {conflictCheck.room_conflicts.map((c, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border border-orange-200">
                      <span className="font-medium">{c.room}</span> - {c.day} Period {c.period}:
                      <span className="text-orange-600 ml-1">{c.classes?.map(cl => cl.class).join(' vs ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="class" className="flex items-center gap-1.5" data-testid="tab-class-view">
            <List className="w-4 h-4" />Class View
          </TabsTrigger>
          <TabsTrigger value="teacher" className="flex items-center gap-1.5" data-testid="tab-teacher-view">
            <User className="w-4 h-4" />Teacher View
          </TabsTrigger>
          <TabsTrigger value="room" className="flex items-center gap-1.5" data-testid="tab-room-view">
            <DoorOpen className="w-4 h-4" />Room View
          </TabsTrigger>
          <TabsTrigger value="master" className="flex items-center gap-1.5" data-testid="tab-master-grid">
            <Grid3X3 className="w-4 h-4" />Master Grid
          </TabsTrigger>
        </TabsList>

        {/* ===== CLASS VIEW ===== */}
        <TabsContent value="class">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Class Timetable</CardTitle>
                  <CardDescription>View weekly schedule for a specific class (Year 7-13)</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedClass} onValueChange={setSelectedClass}>
                    <SelectTrigger className="w-52" data-testid="class-selector">
                      <SelectValue placeholder="Select a class" />
                    </SelectTrigger>
                    <SelectContent>
                      {allClasses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selectedClass && (
                    <Button onClick={handlePrintClass} variant="outline" className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100" data-testid="print-class-btn">
                      <Printer className="w-4 h-4 mr-2" />Print
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedClass && masterSchedule?.[selectedClass] ? (
                renderScheduleTable(masterSchedule[selectedClass], 'teacher')
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a class to view its timetable</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TEACHER VIEW ===== */}
        <TabsContent value="teacher">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Teacher Schedule</CardTitle>
                  <CardDescription>View a teacher's full weekly schedule across all classes</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                    <SelectTrigger className="w-64" data-testid="teacher-selector">
                      <SelectValue placeholder="Select a teacher" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map(t => (
                        <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTeacher && teacherSchedule && (
                    <Button onClick={handlePrintTeacher} variant="outline" className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100" data-testid="print-teacher-btn">
                      <Printer className="w-4 h-4 mr-2" />Print
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedTeacher && selectedTeacherInfo ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className="text-blue-700 border-blue-300">
                      {selectedTeacherInfo.subjects?.length || 0} Subjects
                    </Badge>
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      {selectedTeacherInfo.classes?.length || 0} Classes
                    </Badge>
                    {selectedTeacherInfo.subjects?.map(s => (
                      <Badge key={s} className={getSubjectColor(s)}>{s}</Badge>
                    ))}
                  </div>
                  {renderScheduleTable(teacherSchedule, 'class')}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a teacher to view their schedule</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ROOM VIEW ===== */}
        <TabsContent value="room">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Room Schedule</CardTitle>
                  <CardDescription>View room usage across the week</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                    <SelectTrigger className="w-52" data-testid="room-selector">
                      <SelectValue placeholder="Select a room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(r => (
                        <SelectItem key={r.room} value={r.room}>{r.room} ({r.classes?.length} classes)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRoom && roomSchedule && (
                    <Button onClick={handlePrintRoom} variant="outline" className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100" data-testid="print-room-btn">
                      <Printer className="w-4 h-4 mr-2" />Print
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedRoom && roomSchedule ? (
                renderScheduleTable(roomSchedule, 'class')
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DoorOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a room to view its schedule</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== MASTER GRID ===== */}
        <TabsContent value="master">
          <Card>
            <CardHeader>
              <CardTitle>Master Schedule Grid</CardTitle>
              <CardDescription>Complete overview of all {allClasses.length} classes - Year 7 to 13</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={selectedDay} onValueChange={setSelectedDay} className="w-full">
                <TabsList className="flex flex-wrap justify-start gap-1 mb-4">
                  {DAYS.map(day => (
                    <TabsTrigger key={day} value={day} className="px-4">{day}</TabsTrigger>
                  ))}
                </TabsList>

                {DAYS.map(day => (
                  <TabsContent key={day} value={day}>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-slate-800 text-white">
                            <th className="border border-slate-600 p-2 text-left font-semibold sticky left-0 bg-slate-800 min-w-[90px] z-10">Class</th>
                            {PERIODS.map(period => (
                              <th key={period.number} className="border border-slate-600 p-2 text-center font-semibold min-w-[110px]">
                                <div>{period.label}</div>
                                <div className="text-xs font-normal text-slate-300">{period.start}</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allClasses.map(className => (
                            <tr key={className} className="hover:bg-gray-50">
                              <td className="border p-2 font-semibold sticky left-0 bg-white text-sm z-10">{className}</td>
                              {PERIODS.map(period => (
                                <td key={`${className}-${period.number}`} className="border p-1">
                                  {renderMasterGridCell(className, day, period.number)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Subject Legend */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Subject Legend</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(SUBJECT_COLORS).filter(([k]) => k !== 'default').map(([subject, colorClass]) => (
              <Badge key={subject} variant="outline" className={`${colorClass} border`}>{subject}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
